import { supabase } from './supabase';
import { recordTimeclockEntry } from './timeclock';
import { parseISO, startOfMonth, endOfMonth } from 'date-fns';

// Timezone conversion functions (same as in timeclock.ts)
function localToUTC(date: Date): string {
  return date.toISOString();
}

export interface PayrollEntry {
  id: string;
  resource_id: string;
  resource_first_name: string;
  resource_last_name: string;
  resource_email: string;
  resource_avatar_url?: string;
  compensation_type: string;
  flat_rate_amount: number | null;
  hourly_rate: number | null;
  booking_id: string | null;
  booking_number: number | null;
  booking_title: string | null;
  start_date: string | null;
  end_date: string | null;
  entry_type: 'flat_rate' | 'timeclock' | 'commission' | 'bonus' | 'tip' | 'adjustment';
  /** Rate snapshot stored on the row (hourly or flat rate at the time). */
  rate_amount?: number | null;
  hours: number;
  earnings: number;
  item_name: string | null;
  item_description: string | null;
  created_at: string;
  clock_in?: string;
  clock_out?: string;
  break_start?: string;
  break_end?: string;
  total_minutes?: number;
  // Overtime split (written by the database when overtime is enabled)
  regular_minutes?: number | null;
  overtime_minutes?: number | null;
  double_time_minutes?: number | null;
  overtime_earnings?: number | null;
  clock_in_lat?: number | null;
  clock_in_lng?: number | null;
  clock_out_lat?: number | null;
  clock_out_lng?: number | null;
  clock_source?: string | null;
  needs_review?: boolean;
  review_reason?: string | null;
  /** For adjustments: the paid entry being corrected. */
  adjusts_entry_id?: string | null;
  notes?: string;
  status?: string;
  process_status?: string;
  completed_at?: string;
  booking?: {
    id: string;
    business_id: string;
    booking_number: number;
    title: string;
    status: string;
    created_at: string;
    end_date: string;
    end_time: string;
    completed_at: string;
  };
}

export type PayrollEntryType = PayrollEntry['entry_type'];

/** Which column places an entry in a pay period, per entry type. */
export const PAYROLL_PERIOD_COLUMN: Record<PayrollEntryType, 'clock_in' | 'completed_at' | 'created_at'> = {
  timeclock: 'clock_in',
  flat_rate: 'completed_at',
  commission: 'completed_at',
  bonus: 'created_at',
  tip: 'created_at',
  adjustment: 'created_at',
};

/** Local calendar day bounds as the UTC instants the database is queried with. */
export function payrollPeriodBounds(startDate?: string, endDate?: string): { utcStart: string; utcEnd: string } {
  const localStartDate = startDate ? parseISO(startDate) : startOfMonth(new Date());
  const localEndDate = endDate ? parseISO(endDate) : endOfMonth(new Date());
  localStartDate.setHours(0, 0, 0, 0);
  localEndDate.setHours(23, 59, 59, 999);
  return { utcStart: localToUTC(localStartDate), utcEnd: localToUTC(localEndDate) };
}

export async function getPayrollEntries(
  businessId: string,
  startDate?: string,
  endDate?: string,
  options?: { unpaidOnly?: boolean },
): Promise<PayrollEntry[]> {
  if (!businessId) {
    throw new Error('Business ID is required');
  }
  if (!supabase) {
    throw new Error('Supabase client not initialized');
  }

  const { utcStart, utcEnd } = payrollPeriodBounds(startDate, endDate);

  // One query for every entry type; each type is placed in the period by its own date column.
  const periodFilter = [
    `and(entry_type.eq.timeclock,clock_in.gte.${utcStart},clock_in.lte.${utcEnd})`,
    `and(entry_type.in.(flat_rate,commission),completed_at.gte.${utcStart},completed_at.lte.${utcEnd})`,
    `and(entry_type.in.(bonus,tip,adjustment),created_at.gte.${utcStart},created_at.lte.${utcEnd})`,
  ].join(',');

  let query = supabase
    .from('payroll_page')
    .select(PAYROLL_PAGE_SELECT)
    .eq('business_id', businessId)
    .or(periodFilter);

  // Paid history can fill the default row cap and hide the unpaid rows this list needs.
  if (options?.unpaidOnly) {
    query = query.neq('process_status', 'PAID').neq('process_status', 'VOIDED').limit(10000);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error getting payroll entries:', error);
    throw error;
  }

  return (data || []).map(mapPayrollPageRow).sort((a, b) => periodDate(b).getTime() - periodDate(a).getTime());
}

function periodDate(entry: PayrollEntry): Date {
  if (entry.entry_type === 'timeclock' && entry.clock_in) return new Date(entry.clock_in);
  if (entry.completed_at) return new Date(entry.completed_at);
  return new Date(entry.end_date || entry.created_at);
}

const PAYROLL_PAGE_SELECT = `
  *,
  booking:bookings(
    id,
    business_id,
    booking_number,
    title,
    status,
    created_at,
    end_date,
    end_time,
    completed_at
  ),
  resource:resources(
    first_name,
    last_name,
    email,
    flat_rate_amount,
    hourly_rate,
    compensation_type,
    avatar_url
  ),
  item:flat_rate_items(
    name,
    description
  )
`;

const num = (v: unknown): number => {
  if (v === null || v === undefined || v === '') return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Shape a payroll_page row for the UI. Money and time come straight from the
 * stored columns (the database owns the math); nothing is re-derived here.
 */
function mapPayrollPageRow(entry: any): PayrollEntry {
  const resource = entry.resource;
  const base = {
    id: entry.id,
    resource_id: entry.resource_id,
    resource_first_name: resource?.first_name,
    resource_last_name: resource?.last_name,
    resource_email: resource?.email,
    resource_avatar_url: resource?.avatar_url,
    compensation_type: entry.compensation_type || resource?.compensation_type,
    flat_rate_amount: resource?.flat_rate_amount ?? null,
    hourly_rate: resource?.hourly_rate ?? null,
    rate_amount: entry.rate_amount ?? null,
    booking_id: entry.booking_id,
    booking_number: entry.booking?.booking_number ?? null,
    booking_title: entry.booking?.title ?? null,
    created_at: entry.created_at,
    notes: entry.notes,
    status: entry.status,
    process_status: entry.process_status || 'UNPAID',
    booking: entry.booking,
  };

  if (entry.entry_type === 'timeclock') {
    return {
      ...base,
      start_date: entry.clock_in,
      end_date: entry.clock_out || entry.created_at,
      entry_type: 'timeclock',
      hours: num(entry.total_minutes) / 60,
      earnings: num(entry.total_earnings),
      item_name: null,
      item_description: null,
      completed_at: entry.clock_out,
      clock_in: entry.clock_in,
      clock_out: entry.clock_out,
      break_start: entry.break_start,
      break_end: entry.break_end,
      total_minutes: num(entry.total_minutes),
      regular_minutes: entry.regular_minutes ?? null,
      overtime_minutes: entry.overtime_minutes ?? null,
      double_time_minutes: entry.double_time_minutes ?? null,
      overtime_earnings: entry.overtime_earnings ?? null,
      clock_in_lat: entry.clock_in_lat ?? null,
      clock_in_lng: entry.clock_in_lng ?? null,
      clock_out_lat: entry.clock_out_lat ?? null,
      clock_out_lng: entry.clock_out_lng ?? null,
      clock_source: entry.clock_source ?? null,
      needs_review: !!entry.needs_review,
      review_reason: entry.review_reason ?? null,
    };
  }

  if (entry.entry_type === 'bonus') {
    return {
      ...base,
      start_date: entry.created_at,
      end_date: entry.created_at,
      entry_type: 'bonus',
      hours: 0,
      earnings: num(entry.bonus_earnings ?? entry.total_earnings),
      item_name: entry.item_name || 'Booking Creation Bonus',
      item_description:
        entry.item_description ||
        `Bonus for creating booking #${entry.booking?.booking_number || entry.booking_id}`,
      completed_at: entry.created_at,
    };
  }

  if (entry.entry_type === 'tip') {
    return {
      ...base,
      start_date: entry.created_at,
      end_date: entry.created_at,
      entry_type: 'tip',
      hours: 0,
      earnings: num(entry.tip_earnings ?? entry.total_earnings),
      item_name: entry.item_name || 'Customer Tip',
      item_description: entry.item_description,
      completed_at: entry.created_at,
    };
  }

  if (entry.entry_type === 'commission') {
    return {
      ...base,
      start_date: entry.created_at,
      end_date: entry.completed_at || entry.created_at,
      entry_type: 'commission',
      hours: 0,
      earnings: num(entry.total_earnings ?? entry.total_amount),
      item_name: entry.item_name,
      item_description: entry.item_description,
      completed_at: entry.completed_at,
    };
  }

  if (entry.entry_type === 'adjustment') {
    return {
      ...base,
      start_date: entry.created_at,
      end_date: entry.created_at,
      entry_type: 'adjustment',
      hours: 0,
      earnings: num(entry.total_earnings ?? entry.total_amount),
      item_name: entry.item_name || 'Pay adjustment',
      item_description: entry.item_description,
      completed_at: entry.completed_at || entry.created_at,
      adjusts_entry_id: entry.adjusts_entry_id ?? null,
    };
  }

  // flat_rate
  const hours = num(entry.hours);
  return {
    ...base,
    start_date: entry.created_at,
    end_date: entry.completed_at || entry.created_at,
    entry_type: 'flat_rate',
    hours,
    // Older rows predate the stored total; fall back to the snapshot rate, never the live one.
    earnings: entry.total_earnings === null || entry.total_earnings === undefined
      ? hours * num(entry.rate_amount)
      : num(entry.total_earnings),
    item_name: entry.item?.name,
    item_description: entry.item?.description,
    completed_at: entry.completed_at,
  };
}

/** Paid entries linked to a completed payroll run (for accountant PDF resend). */
export async function getPayrollEntriesForRun(payrollRunId: string): Promise<PayrollEntry[]> {
  if (!payrollRunId) {
    throw new Error('Payroll run ID is required');
  }
  if (!supabase) {
    throw new Error('Supabase client not initialized');
  }

  const { data, error } = await supabase
    .from('payroll_page')
    .select(PAYROLL_PAGE_SELECT)
    .eq('payroll_run_id', payrollRunId);

  if (error) {
    console.error('Error fetching payroll entries for run:', error);
    throw error;
  }

  return (data || []).map(mapPayrollPageRow);
}

export async function updatePayrollEntry(
  id: string,
  updates: {
    clock_in?: string | null;
    clock_out?: string | null;
    break_start?: string | null;
    break_end?: string | null;
    hours?: number;
    rate_amount?: number;
    total_minutes?: number;
    total_earnings?: number;
    bonus_earnings?: number;
    total_amount?: number;
    item_name?: string | null;
    item_description?: string | null;
    notes?: string | null;
    updated_at?: string;
  }
): Promise<any> {
  if (!supabase) throw new Error('Supabase client not initialized');

  console.log('Updating payroll entry:', { id, updates });
  
  try {
    const { data, error } = await supabase
      .from('payroll_page')
      .update(updates)
      .eq('id', id)
      .select(`
        *,
        booking:bookings(
          id,
          business_id,
          booking_number,
          title,
          status,
          created_at,
          end_date,
          end_time,
          completed_at
        ),
        resource:resources(
          first_name,
          last_name,
          email,
          flat_rate_amount,
          hourly_rate,
          compensation_type,
          avatar_url
        ),
        item:flat_rate_items(
          name,
          description
        )
      `)
      .single();

    if (error) {
      console.error('Error updating payroll entry:', error);
      throw error;
    }

    console.log('Payroll entry updated successfully:', data);
    return data;
  } catch (error) {
    console.error('Error updating payroll entry:', error);
    throw error;
  }
}

export async function createPayrollEntry(
  businessId: string,
  resourceId: string,
  entryData: {
    clock_in: string;
    clock_out?: string | null;
    break_start?: string | null;
    break_end?: string | null;
    notes?: string | null;
  }
): Promise<any> {
  if (!supabase) throw new Error('Supabase client not initialized');

  try {
    // The shared timeclock engine snapshots the rate, computes minutes and
    // refuses a second open shift, exactly like a live clock-in would.
    const result = await recordTimeclockEntry(businessId, resourceId, {
      clockIn: entryData.clock_in,
      clockOut: entryData.clock_out ?? null,
      breakStart: entryData.break_start ?? null,
      breakEnd: entryData.break_end ?? null,
      notes: entryData.notes ?? null,
      source: 'admin',
    });

    if (result.already_open) {
      throw new Error('This team member already has an open shift. Clock them out before adding another.');
    }

    const { data, error } = await supabase
      .from('payroll_page')
      .select(`
        *,
        resource:resources(
          first_name,
          last_name,
          email,
          flat_rate_amount,
          hourly_rate,
          compensation_type,
          avatar_url
        )
      `)
      .eq('id', result.entry.id)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating payroll entry:', error);
    throw error;
  }
}

export async function createCustomBonus(
  businessId: string,
  resourceId: string,
  bonus: {
    amount: number;
    item_name?: string | null;
    item_description?: string | null;
    notes?: string | null;
  }
): Promise<any> {
  if (!supabase) throw new Error('Supabase client not initialized');

  const amount = Math.round(Number(bonus.amount) * 100) / 100;
  if (!Number.isFinite(amount) || amount <= 0) {
    throw new Error('Bonus amount must be greater than zero');
  }

  const { data, error } = await supabase
    .from('payroll_page')
    .insert({
      business_id: businessId,
      resource_id: resourceId,
      hours: 0,
      rate_amount: 0,
      total_amount: amount,
      bonus_earnings: amount,
      total_earnings: amount,
      status: 'completed',
      compensation_type: 'bonus',
      entry_type: 'bonus',
      process_status: 'UNPAID',
      item_name: bonus.item_name?.trim() || 'Bonus',
      item_description: bonus.item_description?.trim() || null,
      notes: bonus.notes?.trim() || null,
    })
    .select(`
      *,
      resource:resources(
        first_name,
        last_name,
        email,
        flat_rate_amount,
        hourly_rate,
        compensation_type,
        avatar_url
      )
    `)
    .single();

  if (error) throw error;
  return data;
}

export async function deletePayrollEntry(id: string): Promise<void> {
  if (!supabase) throw new Error('Supabase client not initialized');

  console.log('Deleting payroll entry:', { id });
  
  try {
    // First, get the payroll entry details to check if it's a flat rate entry
    const { data: payrollEntry, error: fetchError } = await supabase
      .from('payroll_page')
      .select('id, entry_type, flat_rate_id, booking_id, resource_id, completed_at, hours')
      .eq('id', id)
      .single();

    if (fetchError) {
      console.error('Error fetching payroll entry:', fetchError);
      throw fetchError;
    }

    console.log('Found payroll entry:', payrollEntry);

    // If it's a flat rate entry, also delete the corresponding booking_flat_rates entry
    if (payrollEntry.entry_type === 'flat_rate' && payrollEntry.flat_rate_id && payrollEntry.booking_id && payrollEntry.resource_id) {
      console.log('Deleting corresponding booking_flat_rates entry:', {
        booking_id: payrollEntry.booking_id,
        resource_id: payrollEntry.resource_id,
        item_id: payrollEntry.flat_rate_id,
        completed_at: payrollEntry.completed_at
      });

      // First, let's find what booking_flat_rates entries exist for this combination
      const { data: existingEntries, error: findError } = await supabase
        .from('booking_flat_rates')
        .select('*')
        .eq('booking_id', payrollEntry.booking_id)
        .eq('resource_id', payrollEntry.resource_id)
        .eq('item_id', payrollEntry.flat_rate_id);

      if (findError) {
        console.error('Error finding booking_flat_rates entries:', findError);
      } else {
        console.log('Found booking_flat_rates entries:', existingEntries);
      }

      // Simple approach: Delete by booking_id, resource_id, and item_id (flat_rate_id)
      // This should delete the specific flat rate item from the booking
      const { error: flatRateDeleteError, count } = await supabase
        .from('booking_flat_rates')
        .delete()
        .eq('booking_id', payrollEntry.booking_id)
        .eq('resource_id', payrollEntry.resource_id)
        .eq('item_id', payrollEntry.flat_rate_id);

      if (flatRateDeleteError) {
        console.error('Error deleting booking_flat_rates entry:', flatRateDeleteError);
        // Don't throw here - continue to delete payroll entry even if flat rate deletion fails
        console.warn('Continuing with payroll entry deletion despite flat rate deletion error');
      } else {
        console.log('Booking flat rate entry deletion result:', { count, deletedCount: count || 0 });
        if (count && count > 0) {
          console.log('Booking flat rate entry deleted successfully');
        } else {
          console.warn('No booking_flat_rates entries were deleted - they may not exist or not match the criteria');
        }
      }
    }

    // Delete the payroll entry
    const { error: deleteError } = await supabase
      .from('payroll_page')
      .delete()
      .eq('id', id);

    if (deleteError) {
      console.error('Error deleting payroll entry:', deleteError);
      throw deleteError;
    }

    console.log('Payroll entry deleted successfully');
  } catch (error) {
    console.error('Error deleting payroll entry:', error);
    throw error;
  }
}